import React from "react";
import { useTranslation } from "react-i18next";
import MainHeader from "../../components/common/MainHeader";
import { List, Form, Space, DatePicker, Typography, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  FileTextFilled,
} from "@ant-design/icons";
import moment from "moment";
import TSTable from "../../components/common/TSTable";
import { POST } from "../../utils/HttpClient";
import { Toast } from "../../utils/Toast";
import InterfaceAXHistoryModal from "../../components/interface_ax/InterfaceAXHistoryModal";
import { INTERFACE_TYPE } from "../../constance/dataTypes";
import { URL_INTERFACE_AX, URL_POST_BAKING } from "../../constance/urls";
import { formatComma } from "../../utils/stringFormat";
import ErrorMessage from "../../components/common/ErrorMessage";
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from "../../constance/strings";
import dayjs from "dayjs";
import { BLUE_COLOR } from "../../constance/color";
import ColorButton from "../../components/common/ColorButton";

export default function InterfaceAXPostBakingWasteWoodCountView() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const tableRef = React.useRef();
  const [loading, setLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [allowInterfaceAX, setAllowInterfaceAX] = React.useState(true);
  const date = Form.useWatch("post_baking_group_date", form);

  const [openHistory, setOpenHistory] = React.useState(false);

  const isEmpty = !date;
  const isFormValid = React.useMemo(
    () => !!date && allowInterfaceAX,
    [date, allowInterfaceAX]
  );

  const columns = React.useMemo(
    () => [
      {
        title: t("post_baking_waste_wood_count.count_date"),
        dataIndex: "created",
        render: (text, record) =>
          moment(record.created).format(HUMAN_DATE_FORMAT),
      },
      {
        title: t("post_baking_waste_wood_count.group_date"),
        dataIndex: "post_baking_group_date",
        render: (text, record) =>
          moment(record.post_baking_group_date).format(HUMAN_DATE_FORMAT),
      },
      {
        title: t("post_baking_waste_wood_count.oven"),
        dataIndex: "ovens",
        render: (text, record) => (
          <div style={{ textAlign: "center" }}>
            {record.ovens ? (
              <CheckOutlined style={{ color: "green" }} />
            ) : (
              <CloseOutlined style={{ color: "red" }} />
            )}
          </div>
        ),
      },
      {
        title: t("wood_size"),
        dataIndex: "wood_size_amount_map_list",
        render: (value) => (
          <List>
            {value
              .filter((data) => data.amount)
              .map((data, index) => (
                <List.Item key={index}>
                  {data.wood_code} {formatComma(data.amount)} {t("units.wood")}
                </List.Item>
              ))}
          </List>
        ),
      },
      {
        title: t("bom_transactions.is_interfaced"),
        dataIndex: "is_interfaced",
        render: (text, record) => (
          <div style={{ textAlign: "center" }}>
            {record.is_interfaced ? (
              <CheckOutlined style={{ color: "green" }} />
            ) : (
              <CloseOutlined style={{ color: "red" }} />
            )}
          </div>
        ),
      },
    ],
    [date]
  );

  const handleDoInterfaceAX = async () => {
    if (!isFormValid) {
      setErrorMessages(t("error_messages.validation.required"));
      return;
    }

    setLoading(true);
    setErrorMessages(null);
    try {
      await POST(URL_INTERFACE_AX.POST_BAKING_WASTE_WOOD_COUNT, {
        date: dayjs(date).format(DATE_FORMAT),
      });
      if (date && tableRef.current) {
        tableRef.current.clear();
      }
      Toast.info(t("progressing"));
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    tableRef.current.fetch();
  }, [date]);

  const handleDataCheck = (data) => {
    setAllowInterfaceAX(data && data.length > 0);
  };

  return (
    <div style={{ margin: "24px", marginTop: "7em" }}>
      <MainHeader />

      <Typography.Title level={2}>
        {t("menus.interface_ax")}:{" "}
        {t("menus.interface_ax_menus.post_baking_waste_wood_count")}
      </Typography.Title>

      <Form layout="inline" form={form}>
        <Space>
          <Form.Item
            label={t("post_baking_waste_wood_count.group_date")}
            name="post_baking_group_date"
            rules={[
              {
                required: true,
                message: t("error_messages.validation.required"),
              },
            ]}
          >
            <DatePicker
              format={HUMAN_DATE_FORMAT}
              placeholder={t("searchs.fields.date")}
            />
          </Form.Item>
          <Form.Item name="interface_ax.history">
            <ColorButton
              variant="outlined"
              color="primary"
              override={BLUE_COLOR}
              icon={<FileTextFilled />}
              onClick={() => setOpenHistory(true)}
            >
              {t("interface_ax.history")}
            </ColorButton>
          </Form.Item>

          <Tooltip
            placement="bottom"
            title={
              !isFormValid ? t("select") + " " + t("searchs.fields.date") : ""
            }
          >
            <ColorButton
              type="primary"
              override={BLUE_COLOR}
              loading={loading}
              disabled={!isFormValid}
              onClick={handleDoInterfaceAX}
            >
              {t("interface_ax.submit")}
            </ColorButton>
          </Tooltip>
        </Space>
      </Form>
      <ErrorMessage message={errorMessages} length={100} />
      <TSTable
        style={{ marginTop: "24px" }}
        scroll={{ x: 1000 }}
        ref={tableRef}
        columns={columns}
        url={date ? URL_POST_BAKING.POST_BAKING_WASTE_WOOD_COUNT : ""}
        manual
        params={{
          date_after: date ? dayjs(date).format(DATE_FORMAT) : null,
          date_before: date ? dayjs(date).format(DATE_FORMAT) : null,
          ...(isEmpty && { isEmpty: true }),
        }}
        onData={handleDataCheck}
      />
      <InterfaceAXHistoryModal
        open={openHistory}
        onClose={() => setOpenHistory(false)}
        type={INTERFACE_TYPE.POST_BAKING_WASTE_WOOD_COUNT}
      />
    </div>
  );
}
